/**
 * Constructor de Ofertas — Visión Global (DEC-003, DEC-014).
 *
 * Pantalla editable en vivo. Estructura:
 *  - Cabecera: empresa · v · fecha · responsable · origen
 *  - Tabs de escenarios (con + Nuevo)
 *  - Columna izquierda (datos): vehículos · parámetros · productos HW · productos SaaS · toggle HW financiado
 *  - Columna derecha (resultados): KPI Valor Activo + 5 acordeones (Cualitativos · Periodo · CAES · Mensual · Inversión)
 *  - Footer: ← volver · Guardar versión · Nueva versión
 *
 * Cálculos en cliente (helpers en este archivo). Los precios en productos guardan snapshot
 * (DEC-010) — no se recalculan al cambiar el catálogo.
 *
 * NO incluye (FUTURO): "Visión MOBILSAFE rentabilidad interna", historial de versiones UI completa,
 * historico de feedback. Esos son post-MVP.
 */
import React, { useState, useEffect, useMemo, useRef, Suspense, lazy } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Plus, Trash2, Loader2, ChevronDown, ChevronUp,
  Star, TrendingUp, DollarSign, Circle, Diamond, History, Search, X, Printer, UserPlus,
  Wallet, Target, RefreshCw,
} from "lucide-react";

// Lazy import: evita que el plugin visual-edits de la plataforma se atasque en tiempo de arranque
const OfertaImprimible = lazy(() => import("./OfertaImprimible"));
import HistorialVersiones from "./HistorialVersiones";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("mobilsafe_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// IMPL-053 · helper para leer email del JWT sin librería extra
function getEmailFromToken() {
  try {
    const t = localStorage.getItem("mobilsafe_token");
    if (!t) return "";
    const payload = JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.email || payload.sub || "";
  } catch {
    return "";
  }
}

const eur = (n) => (Number.isFinite(n) ? n : 0).toLocaleString("es-ES", {
  style: "currency", currency: "EUR", maximumFractionDigits: 0,
});
const pct = (n) => `${(Number.isFinite(n) ? n : 0).toFixed(1)}%`;

// Márgenes mínimos (DEC-011)
// BACKLOG-010 (IMPL-032 · 2026-05-12) · MIN_HW/MIN_SAAS son ahora fallbacks.
// Los valores efectivos vienen de `costes_operativos_ref` (claves `min_margen_hw` / `min_margen_saas`).
// El admin los edita desde Configuración · Parámetros base · Costes operativos de referencia.
const MIN_HW = 25;     // % (fallback)
const MIN_SAAS = 50;   // % (fallback)

const NUEVO_VEHICULO = () => ({
  tipo_id: "", uso_id: "", uds: 1, valor: 25000, consumo: 7, km_anio: 25000, sin_pct: 3,
});
const NUEVO_HW = () => ({
  sku: "", nombre: "Nuevo HW", uds: 1,
  coste_snapshot: 0, precio_snapshot: 0, descuento_pct: 0, precio_final: 0, margen_pct: 0,
});
const NUEVO_SAAS = () => ({ ...NUEVO_HW(), nombre: "Nueva SaaS" });

// IMPL-031 (2026-05-08) · cualitativos dinámicos · ejes mostrados en acordeón "1 · Cualitativos"
const NOMBRES_EJES = {
  seguridad:      "Seguridad y Riesgo",
  combustible:    "Combustible y Conducción",
  operativa:      "Operativa y Control",
  mantenimiento:  "Mantenimiento",
  tic:            "TIC y Digitalización",
  sostenibilidad: "Sostenibilidad y CAES",
};

// Helpers de cálculo (locales — no rompen nada; sirven para alimentar la Visión Cliente)
function calcular(version, costesRef, escenarioIdx, parametrosCaes) {
  const v = version || {};
  const params = v.parametros || {};
  const meses = params.meses_analisis || 36;
  const reducciones = params.reducciones_pct || {};
  const escenario = (v.escenarios || [])[escenarioIdx] || { productos_hw: [], productos_saas: [] };

  // Costes de referencia
  const cmbKey = (params.combustible || "diesel") === "gasolina" ? "combustible_gasolina" : "combustible_diesel";
  const precioCombustible = costesRef.find((c) => c.clave === cmbKey)?.valor || 1.45;
  const costeMantKm = costesRef.find((c) => c.clave === "mantenimiento_km")?.valor || 0.06;
  const costeMulta = costesRef.find((c) => c.clave === "multa_media")?.valor || 180;
  const frecMultas = costesRef.find((c) => c.clave === "frecuencia_multas")?.valor || 0.8;
  const costeSiniestro = costesRef.find((c) => c.clave === "coste_medio_siniestro")?.valor || 5000;
  // IMPL-037 · parámetros de financiación HW (amortización francesa)
  const interesAnual = (costesRef.find((c) => c.clave === "interes_financiacion_pct")?.valor ?? 5) / 100;
  const plazoMeses = costesRef.find((c) => c.clave === "plazo_financiacion_meses")?.valor ?? 36;

  // Vehículos
  let valor_activo = 0, km_total_anio = 0, litros_anio = 0, sin_anio_eur = 0;
  for (const veh of v.vehiculos || []) {
    const uds = veh.uds || 0;
    valor_activo += uds * (veh.valor || 0);
    const km_a = uds * (veh.km_anio || 0);
    km_total_anio += km_a;
    litros_anio += km_a * (veh.consumo || 0) / 100;
    sin_anio_eur += uds * (veh.sin_pct || 0) / 100 * costeSiniestro; // IMPL-031 · ya no hardcoded a 5000
  }

  // Ahorros anuales (€) según % reducciones
  const r_combustible = (reducciones.combustible || 0) / 100;
  const r_mantenimiento = (reducciones.mantenimiento || 0) / 100;
  const r_siniestralidad = (reducciones.siniestralidad || 0) / 100;
  const r_multas = (reducciones.multas || 0) / 100;
  const ahorroFuel_anio = litros_anio * precioCombustible * r_combustible;
  const ahorroMant_anio = km_total_anio * costeMantKm * r_mantenimiento;
  const ahorroSin_anio = sin_anio_eur * r_siniestralidad;
  const totalVehiculos = (v.vehiculos || []).reduce((a, b) => a + (b.uds || 0), 0);
  const ahorroMultas_anio = totalVehiculos * frecMultas * costeMulta * r_multas;
  const ahorro_anio = ahorroFuel_anio + ahorroMant_anio + ahorroSin_anio + ahorroMultas_anio;
  const factorMeses = meses / 12;

  // Productos
  const totalHW = (escenario.productos_hw || []).reduce((a, p) => a + (p.precio_final || 0) * (p.uds || 0), 0);
  const cuotaSaas_mes = (escenario.productos_saas || []).reduce((a, p) => a + (p.precio_final || 0) * (p.uds || 0), 0);
  // IMPL-036 · costes acumulados por línea para tablas P&L del nuevo paso 2
  const costeHW_total = (escenario.productos_hw || []).reduce((a, p) => a + (p.coste_snapshot || 0) * (p.uds || 0), 0);
  // IMPL-038 (2026-06-30) · FIX bug coste SaaS · costeSaaS_mes = coste mensual unitario · costeSaaS_total = prorrateado al periodo completo (igual que cuotasSaas_periodo).
  // Antes el "total" era solo 1 mes y comparaba contra venta_periodo → margen inflado artificialmente.
  const costeSaaS_mes = (escenario.productos_saas || []).reduce((a, p) => a + (p.coste_snapshot || 0) * (p.uds || 0), 0);
  const costeSaaS_total = costeSaaS_mes * meses;
  let cuotaSaas_ajustada_mes = cuotaSaas_mes;
  if (v.hw_financiado && meses > 0) cuotaSaas_ajustada_mes += totalHW / meses;

  const cuotasSaas_periodo = cuotaSaas_ajustada_mes * meses;
  const inversion_puntual = v.hw_financiado ? 0 : totalHW;
  // IMPL-031 (2026-05-08) · si HW se paga puntual, amortizarlo en 60m para Balance Mensual
  const amortizacion_hw_mes = v.hw_financiado ? 0 : (totalHW / 60);

  // CAES · parámetros del admin (DEC-020), con fallback a los defaults si aún no se han cargado
  // o el admin no ha configurado. El comercial sigue viendo lo mismo si no se ha tocado nada.
  const pc = parametrosCaes || {};
  const precioMwh = Number.isFinite(pc.precio_mwh) ? pc.precio_mwh : 145;
  const comisionPct = Number.isFinite(pc.comision_mobilsafe_pct) ? pc.comision_mobilsafe_pct : 20;
  // ratio viene en % (0-100) desde el admin; dentro del cálculo se usa como factor 0-1
  const ratioExito = Number.isFinite(pc.ratio_exito_certificacion) ? (pc.ratio_exito_certificacion / 100) : 1.0;
  // factor L→kWh viene de admin (default 9.7). 1 MWh = 1000 kWh
  const factorLkWh = Number.isFinite(pc.factor_litro_kwh) ? pc.factor_litro_kwh : 9.7;
  // IMPL-038 (2026-06-30) · FIX bug CAES · se certifica UNA SOLA VEZ sobre el ahorro del año 1.
  // NO escala con meses_analisis (antes una oferta a 36m mostraba 3× el ingreso CAES real).
  const ahorroMwhAnual = (litros_anio * r_combustible) * (factorLkWh / 1000);
  const caesPotencial = ahorroMwhAnual * precioMwh * ratioExito;
  const caesComision = caesPotencial * comisionPct / 100;
  const caesNeto = caesPotencial - caesComision;

  // Balances
  const ahorro_periodo = ahorro_anio * factorMeses;
  const balance_operativo = ahorro_periodo - inversion_puntual - cuotasSaas_periodo;
  const balance_mensual = (ahorro_anio / 12) - cuotaSaas_ajustada_mes - amortizacion_hw_mes;
  const ratio_inversion_activo = valor_activo > 0 ? (inversion_puntual / valor_activo) * 100 : 0;

  // IMPL-036 (2026-05-13) · derivados para tablas P&L del nuevo paso 2
  const udsDiv = Math.max(totalVehiculos, 1);
  const mbHW_total = totalHW - costeHW_total;
  // IMPL-038 · margen SaaS en dos versiones: mensual (para tabla por vehículo) y periodo (para tabla por flota).
  // El %MB es invariante (mismo % en mes y en periodo), pero los importes absolutos son distintos.
  const mbSaaS_mes   = cuotaSaas_ajustada_mes - costeSaaS_mes;
  const mbSaaS_total = cuotasSaas_periodo - costeSaaS_total;
  const pctHW   = totalHW > 0 ? (mbHW_total / totalHW) * 100 : 0;
  const pctSaaS = cuotasSaas_periodo > 0 ? (mbSaaS_total / cuotasSaas_periodo) * 100 : 0;
  const ventaFlotaTotal = totalHW + cuotasSaas_periodo;
  const pctTotal = ventaFlotaTotal > 0 ? ((mbHW_total + mbSaaS_total) / ventaFlotaTotal) * 100 : 0;

  // Visión Mobilsafe · Año 1 (HW + 12m SaaS) · Año 2 (12m SaaS, mes 13-24) · Mes
  const ventaMS_anio1 = totalHW + cuotaSaas_ajustada_mes * 12;
  const ventaMS_anio2 = cuotaSaas_ajustada_mes * 12;
  const ventaMS_mes   = cuotaSaas_ajustada_mes;
  // IMPL-038 · costes Visión Mobilsafe usan costeSaaS_mes (no costeSaaS_total) para no duplicar el ×12
  const costeMS_anio1 = costeHW_total + costeSaaS_mes * 12;
  const costeMS_anio2 = costeSaaS_mes * 12;
  const costeMS_mes   = costeSaaS_mes;
  const mbMS_anio1 = ventaMS_anio1 - costeMS_anio1;
  const mbMS_anio2 = ventaMS_anio2 - costeMS_anio2;
  const mbMS_mes   = ventaMS_mes   - costeMS_mes;
  const pctMS_anio1 = ventaMS_anio1 > 0 ? (mbMS_anio1 / ventaMS_anio1) * 100 : 0;
  const pctMS_anio2 = ventaMS_anio2 > 0 ? (mbMS_anio2 / ventaMS_anio2) * 100 : 0;
  const pctMS_mes   = ventaMS_mes   > 0 ? (mbMS_mes   / ventaMS_mes)   * 100 : 0;

  // IMPL-037 · cuota mensual HW por amortización francesa: C × i_mes / (1 - (1+i_mes)^-n)
  const i_mes = interesAnual / 12;
  const cuotaHW_mes_total = (totalHW > 0 && v.hw_financiado && plazoMeses > 0)
    ? (i_mes > 0
        ? totalHW * i_mes / (1 - Math.pow(1 + i_mes, -plazoMeses))
        : totalHW / plazoMeses)
    : 0;
  const amortMes_total = (totalHW > 0 && plazoMeses > 0) ? totalHW / plazoMeses : 0;
  const interesMes_total = Math.max(0, cuotaHW_mes_total - amortMes_total);
  const cuotaHW_mes_uni  = totalVehiculos > 0 ? cuotaHW_mes_total / totalVehiculos : 0;
  const amortMes_uni     = totalVehiculos > 0 ? amortMes_total    / totalVehiculos : 0;
  const interesMes_uni   = totalVehiculos > 0 ? interesMes_total  / totalVehiculos : 0;

  return {
    valor_activo, km_total_anio, litros_anio, totalVehiculos,
    cuotaSaas_mes, cuotaSaas_ajustada_mes, totalHW, inversion_puntual,
    amortizacion_hw_mes,
    cuotasSaas_periodo,
    ahorroFuel_anio, ahorroMant_anio, ahorroSin_anio, ahorroMultas_anio,
    ahorro_anio, ahorro_periodo,
    caesPotencial, caesComision, caesNeto,
    balance_operativo, balance_mensual,
    ratio_inversion_activo,
    // IMPL-036 · campos para tablas paso 2
    costeHW_total, costeSaaS_total,
    costeSaaS_mes,
    mbHW_total, mbSaaS_total, mbSaaS_mes,
    pctHW, pctSaaS, pctTotal,
    precioHW_uni:  totalHW / udsDiv,
    precioSaaS_uni: cuotaSaas_ajustada_mes / udsDiv,
    costeHW_uni:   costeHW_total / udsDiv,
    // IMPL-038 · unitarios SaaS en MES (no periodo) para tabla "Operación por vehículo"
    costeSaaS_uni: costeSaaS_mes / udsDiv,
    mbHW_uni:   mbHW_total / udsDiv,
    mbSaaS_uni: mbSaaS_mes / udsDiv,
    ventaMS_anio1, ventaMS_anio2, ventaMS_mes,
    costeMS_anio1, costeMS_anio2, costeMS_mes,
    mbMS_anio1, mbMS_anio2, mbMS_mes,
    pctMS_anio1, pctMS_anio2, pctMS_mes,
    // IMPL-037 · financiación HW
    cuotaHW_mes_total, cuotaHW_mes_uni,
    interesMes_total,  interesMes_uni,
    amortMes_total,    amortMes_uni,
    interesAnual,      plazoMeses,
  };
}

export default function ConstructorOferta({ ofertaId, cliente, onBack }) {
  const [oferta, setOferta] = useState(null);
  // IMPL-031 · cualitativos dinámicos · cache de ejes con respuestas en la reunión vinculada
  const [ejesRespondidos, setEjesRespondidos] = useState(new Set());
  const [costesRef, setCostesRef] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [usos, setUsos] = useState([]);
  const [catalogo, setCatalogo] = useState([]);     // FASE 3 · catálogo de productos
  const [condiciones, setCondiciones] = useState([]);    // DEC-007 · descuentos y comisiones sugeridas
  const [parametrosCaes, setParametrosCaes] = useState(null); // DEC-020 · precio MWh, factor, comisión, ratio
  const [datosGenerales, setDatosGenerales] = useState(null); // B4.3 · 2026-05-03
  const [activeEsc, setActiveEsc] = useState(0);
  const [pasoConstructor, setPasoConstructor] = useState(1); // IMPL-023 · 1=Vehículos 2=Impactos 3=Cockpit 4=Vista total
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [acc, setAcc] = useState({ cuali: false, per: true, caes: false, men: true, inv: false });
  const [pickerTipo, setPickerTipo] = useState(null);  // "hw" | "saas" | null
  const [vistaImprimir, setVistaImprimir] = useState(false);
  const [vistaHistorial, setVistaHistorial] = useState(false);
  const [mostrarAsignar, setMostrarAsignar] = useState(false);
  const [clientesReales, setClientesReales] = useState([]);
  const [clienteActual, setClienteActual] = useState(cliente || null);

  useEffect(() => { (async () => {
    setLoading(true);
    try {
      const [o, c, t, u, cat, cond, pcaes, dgen] = await Promise.all([
        authAxios.get(`${API}/ofertas/${ofertaId}`),
        authAxios.get(`${API}/configuracion-constructor/costes-operativos`),
        authAxios.get(`${API}/configuracion-constructor/tipos-vehiculo`),
        authAxios.get(`${API}/configuracion-constructor/usos-vehiculo`),
        authAxios.get(`${API}/configuracion-fase2/catalogo?activo=true`).catch(() => ({ data: [] })),
        authAxios.get(`${API}/configuracion-fase2/condiciones`).catch(() => ({ data: [] })),
        authAxios.get(`${API}/configuracion-constructor/parametros-caes`).catch(() => ({ data: null })),
        authAxios.get(`${API}/configuracion-fase2/datos-generales`).catch(() => ({ data: null })),
      ]);
      setOferta(o.data);
      setCostesRef(c.data || []);
      setTipos(t.data || []);
      setUsos(u.data || []);
      setCatalogo(cat.data || []);
      setCondiciones((cond.data || []).filter((x) => x.activo !== false));
      setParametrosCaes(pcaes.data || null);
      setDatosGenerales(dgen.data || null);
    } catch { toast.error("Error cargando oferta"); }
    setLoading(false);
  })(); /* eslint-disable-next-line */ }, [ofertaId]);

  // IMPL-031 · si la oferta tiene reunion_id, leer ejes con respuestas para el acordeón Cualitativos
  useEffect(() => {
    const reunionId = oferta?.reunion_id;
    if (!reunionId) {
      setEjesRespondidos(new Set());
      return;
    }
    authAxios.get(`${API}/reuniones/${reunionId}`)
      .then((r) => {
        const ejes = new Set(
          (r.data?.respuestas_cuestionario || [])
            .map((resp) => resp.eje)
            .filter((e) => Object.prototype.hasOwnProperty.call(NOMBRES_EJES, e))
        );
        setEjesRespondidos(ejes);
      })
      .catch(() => { setEjesRespondidos(new Set()); });
  }, [oferta?.reunion_id]);

  const versionActual = useMemo(() => {
    if (!oferta) return null;
    return (oferta.versiones || []).find((v) => v.version === oferta.version_activa) || (oferta.versiones || [])[0] || null;
  }, [oferta]);

  // Normalizar para que siempre haya escenario y campos coherentes
  useEffect(() => {
    if (!versionActual) return;
    let needsUpdate = false;
    const v = { ...versionActual };
    if (!v.parametros) { v.parametros = { permanencia_m: 36, meses_analisis: 36, combustible: "diesel", reducciones_pct: { combustible: 8, mantenimiento: 5, siniestralidad: 30, multas: 20 } }; needsUpdate = true; }
    if (!v.escenarios || v.escenarios.length === 0) {
      v.escenarios = [{ nombre: "Opción Pro", modalidad: "renting_36m", productos_hw: [], productos_saas: [], condiciones_aplicadas: [], resultados: {} }];
      needsUpdate = true;
    }
    if (needsUpdate) {
      setOferta((o) => ({ ...o, versiones: o.versiones.map((x) => x.version === versionActual.version ? v : x) }));
    }
    // eslint-disable-next-line
  }, [versionActual?.version]);

  const escenario = versionActual?.escenarios?.[activeEsc];
  const calc = useMemo(() => versionActual ? calcular(versionActual, costesRef, activeEsc, parametrosCaes) : null, [versionActual, costesRef, activeEsc, parametrosCaes]);

  // BACKLOG-010 (IMPL-032 · 2026-05-12) · márgenes mínimos editables vía Configuración.
  const minHW = useMemo(() => {
    const c = costesRef.find((x) => x.clave === "min_margen_hw");
    return c && Number.isFinite(Number(c.valor)) ? Number(c.valor) : MIN_HW;
  }, [costesRef]);
  const minSAAS = useMemo(() => {
    const c = costesRef.find((x) => x.clave === "min_margen_saas");
    return c && Number.isFinite(Number(c.valor)) ? Number(c.valor) : MIN_SAAS;
  }, [costesRef]);

  // ------- Mutaciones locales -------
  // IMPL-060 · autoguardado con debounce global compartido.
  // Cualquier mutación reinicia el timer único de 800ms y al disparar guarda
  // el snapshot completo. Usamos un ref para mantener la última versión de
  // `guardar` (closure de React siempre apunta al render más reciente).
  const guardarRef = useRef(null);
  useEffect(() => { guardarRef.current = () => guardar(); });
  const autoSaveTimerRef = useRef(null);
  const scheduleAutoSave = () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;
      if (guardarRef.current) guardarRef.current();
    }, 800);
  };

  function patchVersion(patch) {
    setOferta((o) => ({
      ...o,
      versiones: o.versiones.map((v) => v.version === versionActual.version ? { ...v, ...patch } : v),
    }));
    scheduleAutoSave();
  }
  function patchParametros(patch) { patchVersion({ parametros: { ...versionActual.parametros, ...patch } }); }
  function patchReducciones(k, v) { patchParametros({ reducciones_pct: { ...(versionActual.parametros.reducciones_pct || {}), [k]: v } }); }
  function patchVehiculos(arr) { patchVersion({ vehiculos: arr }); }
  function patchEscenario(idx, patch) {
    patchVersion({ escenarios: versionActual.escenarios.map((e, i) => i === idx ? { ...e, ...patch } : e) });
  }
  function recalcMargenProducto(p) {
    const final = (p.precio_snapshot || 0) * (1 - (p.descuento_pct || 0) / 100);
    const margen = final > 0 && p.coste_snapshot > 0 ? ((final - p.coste_snapshot) / final) * 100 : 0;
    return { ...p, precio_final: Number(final.toFixed(2)), margen_pct: Number(margen.toFixed(1)) };
  }
  function patchProducto(tipo, idx, patch) {
    const lista = (escenario?.[tipo] || []).map((p, i) => i === idx ? recalcMargenProducto({ ...p, ...patch }) : p);
    patchEscenario(activeEsc, { [tipo]: lista });
  }

  // DEC-007 · aplica un descuento sugerido desde Configuración a un producto y deja
  // trazabilidad en `condiciones_aplicadas` del escenario (DEC-012 — explicar el porqué).
  // El comercial puede seguir editando el valor a mano después (campo editable).
  function aplicarCondicion(tipo, idx, condicion) {
    if (!condicion) return;
    const lista = (escenario?.[tipo] || []).map((p, i) =>
      i === idx ? recalcMargenProducto({ ...p, descuento_pct: Number(condicion.valor) || 0 }) : p
    );
    const yaAplicadas = escenario?.condiciones_aplicadas || [];
    const sinRepeticionProducto = yaAplicadas.filter(
      (a) => !(a.tipo === tipo && a.indice_producto === idx && a.condicion_tipo === condicion.tipo)
    );
    const nuevaAplicacion = {
      id: condicion.id,
      condicion_tipo: condicion.tipo,
      nombre: condicion.nombre,
      valor: condicion.valor,
      unidad: condicion.unidad,
      aplicada_a: tipo,
      indice_producto: idx,
      aplicada_at: new Date().toISOString(),
    };
    patchEscenario(activeEsc, {
      [tipo]: lista,
      condiciones_aplicadas: [...sinRepeticionProducto, nuevaAplicacion],
    });
    toast.success(`Aplicado: ${condicion.nombre} (${condicion.valor}${condicion.unidad || ""})`);
  }

  // Helpers específicos por tipo para evitar arrows inline en ProductosCard
  // (el plugin visual-edits se atora trazando arrows inline con closure)
  const patchProductoHw = (idx, patch) => patchProducto("productos_hw", idx, patch);
  const patchProductoSaas = (idx, patch) => patchProducto("productos_saas", idx, patch);
  const delProductoHw = (idx) => patchEscenario(activeEsc, { productos_hw: (escenario?.productos_hw || []).filter((_, i) => i !== idx) });
  const delProductoSaas = (idx) => patchEscenario(activeEsc, { productos_saas: (escenario?.productos_saas || []).filter((_, i) => i !== idx) });
  const aplicarCondicionHw = (idx, condicion) => aplicarCondicion("productos_hw", idx, condicion);
  const aplicarCondicionSaas = (idx, condicion) => aplicarCondicion("productos_saas", idx, condicion);
  const abrirPickerHw = () => setPickerTipo("hw");
  const abrirPickerSaas = () => setPickerTipo("saas");
  const condicionesHw = condiciones.filter((c) => c.tipo === "descuento_hw");
  const condicionesSaas = condiciones.filter((c) => c.tipo === "descuento_saas");

  // ------- Acciones servidor -------
  async function guardar() {
    if (!versionActual) return;
    setSaving(true);
    try {
      // Inyectar resultados calculados al escenario actual antes de guardar (DEC-010 snapshot)
      const versionAEnviar = {
        ...versionActual,
        escenarios: versionActual.escenarios.map((e, i) => i === activeEsc ? {
          ...e,
          resultados: {
            puntual_inversion: calc.inversion_puntual,
            mensual_balance: calc.balance_mensual,
            periodo_balance: calc.balance_operativo,
            caes_neto_cliente: calc.caesNeto,
            caes_comision_mobilsafe: calc.caesComision,
            valor_activo_gestionado: calc.valor_activo,
            ratio_inversion_activo: calc.ratio_inversion_activo,
          },
        } : e),
      };
      await authAxios.put(`${API}/ofertas/${ofertaId}/versiones/${versionActual.version}`, versionAEnviar);
      // Actualizar también importe_actual cache
      await authAxios.put(`${API}/ofertas/${ofertaId}`, { importe_actual: calc.inversion_puntual + calc.cuotasSaas_periodo });
      // IMPL-060 · sin toast.success: el autoguardado se dispara cada 800ms y
      // saturaría la UI. El indicador del header ya comunica el estado.
    } catch {
      // IMPL-060 · error silencioso (no spam con toasts). El indicador se queda
      // en "Guardando..." un momento y vuelve a "✓ Guardado" cuando triunfe el
      // siguiente intento. Si el fallo persiste, el usuario lo verá al hacer F5.
      console.error("Error guardando oferta");
    }
    setSaving(false);
  }

  async function nuevaVersion() {
    if (!confirm("Crear una nueva versión clonando la actual?")) return;
    try {
      const r = await authAxios.post(`${API}/ofertas/${ofertaId}/versiones`);
      setOferta(r.data);
      toast.success(`Versión ${r.data.version_activa} creada`);
    } catch { toast.error("Error creando versión"); }
  }

  // DEC-023 · Asignar cliente real a una oferta borrador
  async function abrirAsignar() {
    try {
      const r = await authAxios.get(`${API}/clientes`);
      setClientesReales((r.data || []).filter((c) => !c.es_borrador));
      setMostrarAsignar(true);
    } catch { toast.error("Error cargando clientes"); }
  }

  async function confirmarAsignar(cliente_id) {
    try {
      await authAxios.put(`${API}/ofertas/${ofertaId}/asignar-cliente`, { cliente_id });
      const [oRes, cRes] = await Promise.all([
        authAxios.get(`${API}/ofertas/${ofertaId}`),
        authAxios.get(`${API}/clientes/${cliente_id}`),
      ]);
      setOferta(oRes.data);
      setClienteActual(cRes.data);
      setMostrarAsignar(false);
      toast.success(`Oferta asignada a ${cRes.data.empresa}`);
    } catch (e) {
      toast.error("Error asignando cliente: " + (e?.response?.data?.detail || ""));
    }
  }

  if (loading || !oferta || !versionActual) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  if (vistaImprimir) {
    return (
      <Suspense fallback={<div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}>
        <OfertaImprimible ofertaId={ofertaId} onBack={() => setVistaImprimir(false)} />
      </Suspense>
    );
  }
  if (vistaHistorial) {
    return <HistorialVersiones oferta={oferta} onBack={() => setVistaHistorial(false)}
      onUpdated={async () => {
        const r = await authAxios.get(`${API}/ofertas/${ofertaId}`);
        setOferta(r.data);
      }} />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* IMPL-054 (2026-05-25) · Header rediseñado en 3 filas con jerarquía visual:
            FILA 1 blanca · FILA 2 verde suave (escenarios) · FILA 3 verde oscuro (stepper) */}
      <header className="sticky top-0 z-20" data-testid="constructor-header">

        {/* FILA 1 · blanca · navegación + nombre + versión inline + acciones */}
        <div className="bg-white border-b border-[#e2e8f0]" data-testid="hdr-row-1">
          <div className="max-w-[1500px] mx-auto px-6 py-2.5 flex items-center gap-3">
            <button onClick={onBack} data-testid="constructor-back-btn"
              className="flex items-center gap-1.5 text-sm text-[#64748b] hover:text-[#0a6640] transition flex-shrink-0">
              <ArrowLeft className="w-4 h-4" /> Volver
            </button>
            <span className="text-[#e2e8f0] flex-shrink-0">/</span>
            <NombreOfertaInline
              value={oferta.nombre || ""}
              fallback={clienteActual?.empresa || cliente?.empresa || "Oferta sin nombre"}
              onSave={async (nuevoNombre, opts = {}) => {
                try {
                  const r = await authAxios.put(`${API}/ofertas/${ofertaId}`, { nombre: nuevoNombre || null });
                  setOferta(r.data);
                  if (!opts.silent) toast.success("Nombre actualizado");
                } catch {
                  toast.error("No se pudo actualizar el nombre");
                }
              }}
            />
            <span className="text-xs text-[#94a3b8] truncate flex-shrink-0" data-testid="hdr-version">
              · <span className="font-bold text-[#0a6640]">v{versionActual.version}</span>
              {" · "}{oferta.estado === "borrador" ? "borrador" : oferta.estado}
              {" · "}{new Date(versionActual.fecha_creacion).toLocaleDateString("es-ES")}
            </span>

            {/* IMPL-039 Fase 2 · Escenarios inline en fila 1 (Header B) */}
            <span className="w-px h-5 bg-[#e2e8f0] flex-shrink-0 mx-1" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#6b9e7e] flex-shrink-0">Escenario:</span>
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="hdr-escenarios-inline">
              {versionActual.escenarios.map((e, i) => (
                <button key={i} onClick={() => setActiveEsc(i)} data-testid={`escenario-tab-${i}`}
                  className={`text-xs transition flex-shrink-0 ${
                    activeEsc === i
                      ? "bg-[#0a6640] text-white font-bold shadow-sm"
                      : "bg-white text-[#475569] border border-[#d1fae5] hover:border-[#a7f3d0] font-medium"
                  }`}
                  style={{ padding: "4px 12px", borderRadius: "20px" }}>
                  {e.nombre || `Escenario ${i + 1}`}
                </button>
              ))}
              <button onClick={() => {
                const escenarioBase = versionActual.escenarios[activeEsc] || {};
                const nuevoEscenario = {
                  ...JSON.parse(JSON.stringify(escenarioBase)),
                  nombre: `Escenario ${versionActual.escenarios.length + 1}`,
                };
                patchVersion({ escenarios: [...versionActual.escenarios, nuevoEscenario] });
                setActiveEsc(versionActual.escenarios.length);
              }} data-testid="escenario-nuevo-btn"
                className="text-xs font-medium border-dashed border-2 text-[#6b9e7e] hover:bg-white transition flex-shrink-0 inline-flex items-center gap-0.5"
                style={{ padding: "3px 10px", borderRadius: "20px", borderColor: "#a7f3d0" }}>
                <Plus className="w-3 h-3" /> Nuevo
              </button>
            </div>

            <div className="flex-1" />

            {/* Botón asignar cliente · solo icono naranja · solo si oferta independiente */}
            {oferta.tipo === "independiente" && (
              <>
                <button
                  onClick={() => abrirAsignar()}
                  data-testid="btn-asignar-cliente"
                  title="Asignar a cliente real"
                  className="text-white rounded-md inline-flex items-center justify-center hover:opacity-90 transition flex-shrink-0"
                  style={{ background: "#ec561c", padding: "6px 10px" }}
                >
                  <UserPlus className="w-4 h-4" />
                </button>
                <span className="w-px h-6 bg-[#e2e8f0] flex-shrink-0" />
              </>
            )}

            <div
              data-testid="constructor-autosave-status"
              className={`text-sm px-4 py-1.5 rounded-lg flex items-center gap-1.5 font-semibold flex-shrink-0 ${saving ? "bg-slate-100 text-slate-600" : "bg-[#f0fdf4] text-[#0a6640] border border-[#d1fae5]"}`}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Guardando...
                </>
              ) : (
                <>
                  <span className="w-4 h-4 flex items-center justify-center text-base leading-none">✓</span> Guardado
                </>
              )}
            </div>
            <button onClick={() => setVistaImprimir(true)} data-testid="constructor-imprimir-btn"
              className="text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 flex-shrink-0">
              <Printer className="w-4 h-4" /> Imprimir
            </button>
            <button onClick={nuevaVersion} data-testid="constructor-nueva-version-btn"
              className="text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 flex-shrink-0">
              <Plus className="w-4 h-4" /> Versión
            </button>
            <button onClick={() => setVistaHistorial(true)} data-testid="constructor-historial-btn"
              className="text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 flex-shrink-0">
              <History className="w-4 h-4" /> Historial versiones
            </button>
          </div>
        </div>

        {/* FILA 2 · IMPL-039 Fase 2 · banda KPIs vivos del escenario activo (Header B) · IMPL-064: solo en Paso 1 (en Paso 2 se duplicaba con las zonas KPI zonificadas) */}
        {pasoConstructor !== 2 && (
        <div className="bg-gradient-to-r from-emerald-50 via-emerald-50/60 to-emerald-50 border-b border-emerald-100" data-testid="hdr-kpis-vivos">
          <div className="max-w-[1500px] mx-auto px-6 py-3 grid grid-cols-3 gap-4">
            <KpiVivoHdr
              Icon={TrendingUp}
              label="Ahorro periodo"
              valor={eur(calc.ahorro_periodo)}
              color="emerald"
            />
            <KpiVivoHdr
              Icon={Wallet}
              label="Margen bruto"
              valor={eur(calc.mbHW_total + calc.mbSaaS_total)}
              delta={`${(calc.pctTotal || 0).toFixed(1).replace(".", ",")} %`}
              color="amber"
            />
            <KpiVivoHdr
              Icon={Target}
              label={calc.balance_mensual > 0 ? "ROI · Recuperación" : "Balance mensual"}
              valor={calc.balance_mensual > 0
                ? `${Math.max(1, Math.round((calc.inversion_puntual || 0) / Math.max(calc.balance_mensual, 1)))} meses`
                : eur(calc.balance_mensual)
              }
              color="blue"
            />
          </div>
        </div>
        )}

        {/* FILA 3 · verde oscuro · stepper con CONTRASTE FUERTE (IMPL-039) */}
        <div className="flex bg-[#0a6640]" data-testid="constructor-stepper">
          {[
            { n: 1, label: "Configurar" },
            { n: 2, label: "Números" },
          ].map((p, idx) => {
            const isActive = pasoConstructor === p.n;
            return (
              <button key={p.n}
                onClick={() => setPasoConstructor(p.n)}
                data-testid={`constructor-paso-${p.n}`}
                className={`flex-1 py-3 text-sm transition relative inline-flex items-center justify-center gap-2 ${
                  isActive
                    ? "bg-white text-[#0a6640] font-black shadow-inner"
                    : "text-emerald-100/70 hover:text-white hover:bg-white/10 font-medium"
                } ${idx > 0 ? "border-l border-emerald-700/50" : ""}`}>
                <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[11px] font-black ${
                  isActive ? "bg-[#0a6640] text-white" : "bg-emerald-700/30 text-emerald-100/80"
                }`}>{p.n}</span>
                {p.label}
                {isActive && <span className="absolute bottom-0 left-0 right-0 h-1 bg-emerald-400" />}
              </button>
            );
          })}
        </div>
      </header>

      {/* PASO 1 · IMPL-053 (orden) · Modalidad → Vehículos → HW */}
      {pasoConstructor === 1 && (
        <main className="max-w-[1500px] mx-auto px-6 py-6 space-y-4" data-testid="constructor-paso-1-main">
          {/* IMPL-060 · banda de paso al ancho completo · sustituye al SectionHeader */}
          <div className="w-full bg-[#f0fdf4] border border-[#d1fae5] rounded-lg px-5 py-3 flex items-center gap-3 mb-2" data-testid="constructor-banda-paso-1">
            <span className="text-xs font-bold uppercase tracking-wide bg-[#0a6640] text-white px-3 py-1 rounded-md">
              Paso 1
            </span>
            <span className="text-sm font-medium text-[#374151]">
              Configura los datos de la oferta — modalidad, flota y productos
            </span>
          </div>

          <Card title="Modalidad y contrato" className="bg-[#f0fdf4] border-green-100">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <NumField label="Permanencia (meses)" value={versionActual.parametros.permanencia_m} onChange={(v) => patchParametros({ permanencia_m: +v })} />
              <NumField label="Meses análisis" value={versionActual.parametros.meses_analisis} onChange={(v) => patchParametros({ meses_analisis: +v })} />
              <SelectField label="Combustible" value={versionActual.parametros.combustible} onChange={(v) => patchParametros({ combustible: v })}
                options={[["diesel", "Diésel"], ["gasolina", "Gasolina"], ["mixto", "Mixto"]]} />
              <SelectField label="Modalidad SaaS" value={versionActual.modalidad_saas} onChange={(v) => patchVersion({ modalidad_saas: v })}
                options={[["estandar", "Estándar"], ["prepago_6m", "Prepago 6m"], ["prepago_12m", "Prepago 12m"]]} />
            </div>
            <div className="mt-3 p-3 bg-white/60 rounded-lg flex items-center gap-3 flex-wrap" data-testid="hw-financiado-toggle-p1">
              <span className="text-sm text-slate-700 font-medium">Modalidad HW:</span>
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => patchVersion({ hw_financiado: false })}
                  className={`px-4 py-2 text-sm font-medium transition ${!versionActual.hw_financiado ? "bg-[#0a6640] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                  data-testid="toggle-venta-hw-p1">
                  Venta HW
                </button>
                <button
                  type="button"
                  onClick={() => patchVersion({ hw_financiado: true })}
                  className={`px-4 py-2 text-sm font-medium transition ${versionActual.hw_financiado ? "bg-[#0a6640] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                  data-testid="toggle-financiacion-hw-p1">
                  Financiación HW
                </button>
              </div>
              <span className="text-xs text-slate-500">(prorratea HW+Inst en cuota SaaS · DEC-017)</span>
            </div>
          </Card>

          <Card title="Vehículos">
            {(versionActual.vehiculos || []).map((veh, i) => (
              <VehiculoRow key={i} veh={veh} tipos={tipos} usos={usos}
                onChange={(p) => patchVehiculos(versionActual.vehiculos.map((x, j) => j === i ? { ...x, ...p } : x))}
                onDelete={() => patchVehiculos(versionActual.vehiculos.filter((_, j) => j !== i))}
                idx={i}
              />
            ))}
            <button onClick={() => patchVehiculos([...(versionActual.vehiculos || []), NUEVO_VEHICULO()])}
              data-testid="constructor-add-vehiculo-p1" className="inline-flex items-center gap-1 py-2 px-3 text-sm border-dashed border-2 rounded-lg text-slate-500 hover:bg-slate-50 self-start">
              <Plus className="w-4 h-4" /> Añadir tipo de vehículo
            </button>
          </Card>

          <ProductosCard
            tipo="productos_hw" titulo="Productos HW" testid="hw-p1"
            lista={escenario?.productos_hw || []} minMargen={minHW}
            condicionesSugeridas={condicionesHw}
            onAplicarCondicion={aplicarCondicionHw}
            onAdd={abrirPickerHw}
            onChange={patchProductoHw}
            onDelete={delProductoHw}
          />
          <ProductosCard
            tipo="productos_saas" titulo="Productos SaaS" testid="saas-p1"
            lista={escenario?.productos_saas || []} minMargen={minSAAS}
            condicionesSugeridas={condicionesSaas}
            onAplicarCondicion={aplicarCondicionSaas}
            onAdd={abrirPickerSaas}
            onChange={patchProductoSaas}
            onDelete={delProductoSaas}
          />

          {/* IMPL-036 · % reducciones movidas aquí desde el paso 2 antiguo */}
          {/* IMPL-060 · estética unificada con los Cards (bg light, slate normal) */}
          <div className="bg-[#f8fafc] border border-slate-200 rounded-lg p-4" data-testid="cockpit-reducciones-block-p2">
            <p className="text-xs font-medium mb-1 text-slate-700">% reducciones estimadas</p>
            <p className="text-xs text-slate-500 mb-3">Estos porcentajes alimentan los cálculos del paso 2 · Números.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <NumField label="Combustible (%)" value={versionActual.parametros.reducciones_pct?.combustible || 0} onChange={(v) => patchReducciones("combustible", +v)} step="0.1" testid="red-combustible-p2" />
              <NumField label="Mantenimiento (%)" value={versionActual.parametros.reducciones_pct?.mantenimiento || 0} onChange={(v) => patchReducciones("mantenimiento", +v)} step="0.1" testid="red-mantenimiento-p2" />
              <NumField label="Siniestralidad (%)" value={versionActual.parametros.reducciones_pct?.siniestralidad || 0} onChange={(v) => patchReducciones("siniestralidad", +v)} step="0.1" testid="red-siniestralidad-p2" />
              <NumField label="Multas (%)" value={versionActual.parametros.reducciones_pct?.multas || 0} onChange={(v) => patchReducciones("multas", +v)} step="0.1" testid="red-multas-p2" />
            </div>
          </div>

          <NavPasoConstructor paso={1} setPaso={setPasoConstructor} />
        </main>
      )}

      {/* IMPL-036 (2026-05-13) · PASO 2 · Números (P&L interno + argumento cliente en 2 cols) */}
      {pasoConstructor === 2 && (
        <main className="max-w-[1500px] mx-auto px-6 py-6" data-testid="constructor-paso-2-main">

          {/* IMPL-060 · banda de paso al ancho completo */}
          <div className="w-full bg-[#eef2ff] border border-[#c7d2fe] rounded-lg px-5 py-3 flex items-center gap-3 mb-4" data-testid="constructor-banda-paso-2">
            <span className="text-xs font-bold uppercase tracking-wide bg-[#3730a3] text-white px-3 py-1 rounded-md">
              Paso 2
            </span>
            <span className="text-sm font-medium text-[#374151]">
              Revisa los números internos y el argumento de venta al cliente
            </span>
          </div>

          {/* IMPL-064 (2026-06-30) · Resumen sticky reemplazado por 2 zonas KPI alineadas con sus columnas (Visión MOBILSAFE · Visión cliente) */}

          {/* IMPL-064 · 2 zonas KPI con paletas distintas (navy MS · verde cliente), alineadas con sus columnas */}
          <ZonasKPI calc={calc} mesesAnalisis={versionActual.parametros.meses_analisis} hwFinanciado={!!versionActual.hw_financiado} />

          {/* IMPL-064 · Títulos de columna alineados (navy MS · verde cliente) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4" data-testid="constructor-titulos-columna">
            <div className="bg-[#1e3a5f] text-white rounded-lg px-5 py-3 flex items-center gap-3 shadow">
              <Target className="w-5 h-5" />
              <h2 className="text-base font-black uppercase tracking-widest">Visión MOBILSAFE</h2>
            </div>
            <div className="bg-[#0a6640] text-white rounded-lg px-5 py-3 flex items-center gap-3 shadow">
              <Diamond className="w-5 h-5" />
              <h2 className="text-base font-black uppercase tracking-widest">Visión cliente</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* COLUMNA IZQUIERDA · Mobilsafe interno */}
            <div className="space-y-4" data-testid="col-mobilsafe">
              {/* IMPL-060 · párrafo "Mobilsafe · interno" sustituido por la banda de paso superior */}

              {/* B1 · Operación por vehículo · IMPL-064 · añadida 3ª columna "Total mes" (HW prorrateado + SaaS mes) */}
              <BloqueTabla titulo="Operación por vehículo" colorHeader="bg-orange-100 text-orange-900"
                headers={["", "HW", "SaaS", `Total mes (×${versionActual.parametros.meses_analisis}m)`]} testid="tabla-por-vehiculo">
                <FilasB1Operacion calc={calc} mesesAnalisis={versionActual.parametros.meses_analisis} hwFinanciado={!!versionActual.hw_financiado} minHW={minHW} minSAAS={minSAAS} />
              </BloqueTabla>

              {/* B2 · Operación por flota */}
              <BloqueTabla
                titulo={`Operación por flota · ${calc.totalVehiculos} veh · ${versionActual.parametros.meses_analisis}m`}
                colorHeader="bg-blue-100 text-blue-900"
                headers={["", "HW", "SaaS", "Total"]} testid="tabla-por-flota">
                <FilaTabla label="Venta" cols={[eur(calc.totalHW),       eur(calc.cuotasSaas_periodo), eur(calc.totalHW + calc.cuotasSaas_periodo)]} />
                <FilaTabla label="Coste" cols={[eur(calc.costeHW_total), eur(calc.costeSaaS_total),    eur(calc.costeHW_total + calc.costeSaaS_total)]} />
                <FilaTabla label="MB"    cols={[eur(calc.mbHW_total),    eur(calc.mbSaaS_total),       eur(calc.mbHW_total + calc.mbSaaS_total)]} negrita />
                <FilaTabla label="%MB"   cols={[pct(calc.pctHW),         pct(calc.pctSaaS),            pct(calc.pctTotal)]} />
                <FilaTabla label="Check" cols={[<Check key="hw" val={calc.pctHW} min={minHW} />, <Check key="saas" val={calc.pctSaaS} min={minSAAS} />, <Check key="tot" val={calc.pctTotal} min={minHW} />]} ultima />
              </BloqueTabla>

              {/* B3 · Visión anual (anio1 / anio2 / mes) · IMPL-064 · renombrado de "Visión Mobilsafe" */}
              <BloqueTabla titulo="Visión anual" colorHeader="bg-emerald-100 text-emerald-900"
                headers={["", "Año 1", "Año 2", "Mes"]} testid="tabla-vision-mobilsafe">
                <FilaTabla label="Venta" cols={[eur(calc.ventaMS_anio1), eur(calc.ventaMS_anio2), eur(calc.ventaMS_mes)]} />
                <FilaTabla label="Coste" cols={[eur(calc.costeMS_anio1), eur(calc.costeMS_anio2), eur(calc.costeMS_mes)]} />
                <FilaTabla label="MB"    cols={[eur(calc.mbMS_anio1),    eur(calc.mbMS_anio2),    eur(calc.mbMS_mes)]} negrita />
                <FilaTabla label="%MB"   cols={[pct(calc.pctMS_anio1),   pct(calc.pctMS_anio2),   pct(calc.pctMS_mes)]} />
                <FilaTabla label="Check" cols={[<Check key="a1" val={calc.pctMS_anio1} min={minHW} />, <Check key="a2" val={calc.pctMS_anio2} min={minHW} />, <Check key="ms" val={calc.pctMS_mes} min={minHW} />]} ultima />
              </BloqueTabla>

              {/* B4 · Inversión y gasto cliente · IMPL-064 · MOVIDO a columna derecha (arriba) */}

              {/* IMPL-037 · Estudio de crédito (solo en modo financiación HW) */}
              {versionActual.hw_financiado && calc.totalHW > 0 && (
                <BloqueTabla titulo={`Estudio crédito · amort. francesa · ${calc.plazoMeses}m`}
                  colorHeader="bg-amber-100 text-amber-900" headers={["", "Flota", "Por vehículo"]}
                  testid="tabla-estudio-credito">
                  <FilaTabla label="Capital"          cols={[eur(calc.totalHW),         eur(calc.totalHW / Math.max(calc.totalVehiculos, 1))]} />
                  <FilaTabla label="TAE"              cols={[`${(calc.interesAnual * 100).toFixed(2)}%`, "—"]} />
                  <FilaTabla label="Plazo (meses)"    cols={[calc.plazoMeses,           "—"]} />
                  <FilaTabla label="Amortización mes" cols={[eur(calc.amortMes_total),  eur(calc.amortMes_uni)]} />
                  <FilaTabla label="Intereses mes"    cols={[eur(calc.interesMes_total), eur(calc.interesMes_uni)]} />
                  <FilaTabla label="Cuota mes"        cols={[eur(calc.cuotaHW_mes_total), eur(calc.cuotaHW_mes_uni)]} negrita />
                  <FilaTabla label="Intereses total"  cols={[eur(calc.interesMes_total * calc.plazoMeses), "—"]} ultima />
                </BloqueTabla>
              )}
            </div>

            {/* COLUMNA DERECHA · argumento de venta · IMPL-064 · título suprimido (ya hay título columna grande arriba) */}
            <div className="space-y-4" data-testid="col-cliente">

              {/* B4 · Inversión y gasto cliente · IMPL-064 · MOVIDO desde columna izquierda y renombrado */}
              <BloqueTabla titulo="Inversión y gasto cliente" colorHeader="bg-[#0a6640] text-white"
                headers={["", "Inversión", "Mes"]} testid="tabla-inversion-gasto-cliente">
                <FilaTabla
                  label="Total HW"
                  cols={[
                    versionActual.hw_financiado ? "Financiado" : eur(calc.totalHW),
                    versionActual.hw_financiado ? eur(calc.cuotaHW_mes_total) : "—",
                  ]}
                />
                <FilaTabla label="Total por mes"        cols={["—", eur(calc.cuotaSaas_ajustada_mes + (versionActual.hw_financiado ? calc.cuotaHW_mes_total : 0))]} />
                <FilaTabla label="Por vehículo y mes"   cols={["—", eur((calc.cuotaSaas_ajustada_mes + (versionActual.hw_financiado ? calc.cuotaHW_mes_total : 0)) / Math.max(calc.totalVehiculos, 1))]} negrita ultima />
              </BloqueTabla>

              {/* B5 · Ahorro por área · año */}
              <BloqueTabla titulo="Ahorro por área · año" colorHeader="bg-slate-100 text-slate-700"
                headers={["", "%", "Ahorro/año"]} testid="tabla-ahorro-area">
                <FilaTabla label="Combustible"     cols={[pct(versionActual.parametros.reducciones_pct?.combustible || 0),    eur(calc.ahorroFuel_anio)]} verde />
                <FilaTabla label="Mantenimiento"   cols={[pct(versionActual.parametros.reducciones_pct?.mantenimiento || 0),  eur(calc.ahorroMant_anio)]} verde />
                <FilaTabla label="Siniestralidad" cols={[pct(versionActual.parametros.reducciones_pct?.siniestralidad || 0), eur(calc.ahorroSin_anio)]} verde />
                <FilaTabla label="Multas"          cols={[pct(versionActual.parametros.reducciones_pct?.multas || 0),         eur(calc.ahorroMultas_anio)]} verde />
                <FilaTabla label="Total ahorro/año" cols={["", eur(calc.ahorro_anio)]} ultima negrita verde />
              </BloqueTabla>

              {/* B6 · Balance cliente · periodo */}
              <BloqueTabla titulo={`Balance cliente · ${versionActual.parametros.meses_analisis}m`}
                colorHeader="bg-slate-100 text-slate-700" headers={["", "€"]} testid="tabla-balance-cliente">
                {/* IMPL-037 · en modo financiación no hay inversión puntual · aparece cuota HW total del periodo */}
                {!versionActual.hw_financiado && (
                  <FilaTabla label="Inversión HW" cols={[eur(-calc.inversion_puntual)]} rojo />
                )}
                {versionActual.hw_financiado && (
                  <FilaTabla label={`Cuota HW · ${Math.min(calc.plazoMeses, versionActual.parametros.meses_analisis)}m`}
                    cols={[eur(-calc.cuotaHW_mes_total * Math.min(calc.plazoMeses, versionActual.parametros.meses_analisis))]} rojo />
                )}
                <FilaTabla label={`Cuotas SaaS · ${versionActual.parametros.meses_analisis}m`}  cols={[eur(-calc.cuotasSaas_periodo)]} rojo />
                <FilaTabla label={`Ahorro total · ${versionActual.parametros.meses_analisis}m`} cols={[eur(calc.ahorro_periodo)]} verde />
                <FilaTabla label="Balance neto"                                 cols={[eur(calc.balance_operativo)]} ultima negrita verde={calc.balance_operativo >= 0} rojo={calc.balance_operativo < 0} />
              </BloqueTabla>

              {/* B7 · ROI y recuperación */}
              <BloqueTabla titulo="ROI y recuperación" colorHeader="bg-violet-100 text-violet-900"
                headers={["", ""]} testid="tabla-roi">
                <FilaTabla label="Recuperación inversión" cols={[
                  calc.balance_mensual > 0
                    ? `${((calc.inversion_puntual + calc.cuotasSaas_periodo) / Math.max(calc.balance_mensual, 1)).toFixed(1)} meses`
                    : "—"
                ]} />
                <FilaTabla label="Ahorro mensual neto"    cols={[eur(calc.balance_mensual)]} verde={calc.balance_mensual >= 0} rojo={calc.balance_mensual < 0} />
                <FilaTabla label="Ahorro por veh. y mes"  cols={[eur(calc.balance_mensual / Math.max(calc.totalVehiculos, 1))]} ultima verde={calc.balance_mensual >= 0} rojo={calc.balance_mensual < 0} negrita />
              </BloqueTabla>

              {/* B8 · CAES */}
              <BloqueTabla titulo="Potencial CAES · Año 1 (certificación única)"
                colorHeader="bg-[#0a6640] text-white" headers={["", "€"]} testid="tabla-caes">
                <FilaTabla label="Certificados potenciales"   cols={[eur(calc.caesPotencial)]} />
                <FilaTabla label="Comisión Mobilsafe 20%"     cols={[eur(-calc.caesComision)]} rojo />
                <FilaTabla label="Ingreso neto cliente"       cols={[eur(calc.caesNeto)]} ultima negrita verde />
              </BloqueTabla>
            </div>
          </div>

          <NavPasoConstructor paso={2} setPaso={setPasoConstructor} />
        </main>
      )}

      {/* IMPL-036 · paso 3 antiguo (Cockpit) y paso 4 antiguo (grid) eliminados. */}
      

      {/* Picker de catálogo (FASE 3 P1.3) */}
      {pickerTipo && (
        <PickerCatalogo
          tipo={pickerTipo}
          catalogo={catalogo}
          onClose={() => setPickerTipo(null)}
          onPick={(producto) => {
            const target = pickerTipo === "hw" ? "productos_hw" : "productos_saas";
            // IMPL-030 (2026-05-06) · uds inicial = total vehículos (el comercial puede ajustar después)
            const udsDefault = (versionActual.vehiculos || []).reduce((a, b) => a + (b.uds || 0), 0) || 1;
            // Snapshot inmediato (DEC-010): coste/precio del catálogo se congelan en la oferta
            const nuevo = recalcMargenProducto({
              sku: producto.sku,
              nombre: producto.nombre,
              uds: udsDefault,
              coste_snapshot: producto.coste,
              precio_snapshot: producto.precio,
              descuento_pct: 0,
              precio_final: 0,
              margen_pct: 0,
            });
            patchEscenario(activeEsc, { [target]: [...(escenario?.[target] || []), nuevo] });
            setPickerTipo(null);
            toast.success(`${producto.nombre} añadido (snapshot · ${udsDefault} uds)`);
          }}
          onAddManual={() => {
            const target = pickerTipo === "hw" ? "productos_hw" : "productos_saas";
            // IMPL-030 · uds inicial también en producto manual
            const udsDefault = (versionActual.vehiculos || []).reduce((a, b) => a + (b.uds || 0), 0) || 1;
            const base = pickerTipo === "hw" ? NUEVO_HW() : NUEVO_SAAS();
            base.uds = udsDefault;
            patchEscenario(activeEsc, { [target]: [...(escenario?.[target] || []), recalcMargenProducto(base)] });
            setPickerTipo(null);
          }}
        />
      )}

      {/* DEC-023 · Modal asignar cliente real */}
      {mostrarAsignar && (
        <ModalAsignarCliente
          clientesReales={clientesReales}
          onCerrar={() => setMostrarAsignar(false)}
          onAsignar={confirmarAsignar}
        />
      )}
    </div>
  );
}

function ModalAsignarCliente({ clientesReales, onCerrar, onAsignar }) {
  const [q, setQ] = useState("");
  const filtrados = clientesReales.filter((c) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (c.empresa || "").toLowerCase().includes(s) || (c.cif || "").toLowerCase().includes(s);
  });
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="modal-asignar-cliente"
      onClick={onCerrar}>
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-base font-bold">Asignar cliente real</h2>
            <p className="text-xs text-slate-500">La oferta se moverá del cliente borrador al cliente que elijas (DEC-023).</p>
          </div>
          <button onClick={onCerrar} className="p-1.5 hover:bg-slate-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-2 border rounded-lg px-2">
            <Search className="w-4 h-4 text-slate-400" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="modal-asignar-buscar"
              placeholder="Buscar cliente por empresa o CIF…"
              className="flex-1 px-2 py-2 text-sm focus:outline-none" />
          </div>
          <div className="max-h-72 overflow-y-auto border rounded-lg divide-y">
            {clientesReales.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">
                No tienes clientes reales todavía. Crea uno desde la sección Clientes.
              </div>
            ) : filtrados.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">Sin resultados</div>
            ) : filtrados.map((c) => (
              <button key={c.id}
                onClick={() => onAsignar(c.id)}
                data-testid={`asignar-cliente-${c.id}`}
                className="w-full px-4 py-3 text-left hover:bg-emerald-50 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-sm">{c.empresa || "(sin nombre)"}</div>
                  <div className="text-xs text-slate-500">{c.cif || "—"}</div>
                </div>
                <span className="text-xs text-emerald-700 font-semibold">Asignar →</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===================== Sub-componentes =====================

function SectionHeader({ children, hiddenOnDesktop = false }) {
  // FIX-UX-1 · cuando es título del paso (redundante con stepper) se oculta en md+
  const cls = hiddenOnDesktop ? "md:hidden " : "";
  return <h2 className={`${cls}text-sm font-bold uppercase tracking-wide text-slate-500 px-1`}>{children}</h2>;
}

function Card({ title, children, className = "" }) {
  return (
    <div className={`bg-white rounded-xl border p-4 ${className}`} data-testid={title ? `card-${title.toLowerCase().replace(/\s+/g, "-")}` : undefined}>
      <h3 className="font-semibold text-slate-900 mb-3">{title}</h3>
      {children}
    </div>
  );
}

// IMPL-053 · Nombre de oferta editable inline en el header
// IMPL-053b (2026-05-25) · autosave debounce 1s mientras se edita
// IMPL-054 (2026-05-25) · header ahora blanco · estilos en oscuro
function NombreOfertaInline({ value, fallback, onSave }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value || "");
  const lastSavedRef = React.useRef(value || "");
  const cancelRef = React.useRef(false);
  React.useEffect(() => { setDraft(value || ""); lastSavedRef.current = value || ""; }, [value]);

  React.useEffect(() => {
    if (!editing) return;
    if (cancelRef.current) return;
    const next = draft.trim();
    if (next === (lastSavedRef.current || "").trim()) return;
    const t = setTimeout(() => {
      lastSavedRef.current = next;
      onSave(next, { silent: true });
    }, 1000);
    return () => clearTimeout(t);
  }, [draft, editing, onSave]);

  const display = value || fallback;
  if (editing) {
    return (
      <input
        type="text"
        value={draft}
        onChange={(e) => { cancelRef.current = false; setDraft(e.target.value); }}
        onBlur={() => {
          setEditing(false);
          if (cancelRef.current) { cancelRef.current = false; return; }
          const next = draft.trim();
          if (next !== (lastSavedRef.current || "").trim()) {
            lastSavedRef.current = next;
            onSave(next);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            cancelRef.current = true;
            setDraft(lastSavedRef.current || "");
            setEditing(false);
          }
        }}
        autoFocus
        data-testid="hdr-nombre-input"
        className="bg-transparent border-b-2 border-[#0a6640] focus:outline-none px-1 py-0.5 min-w-[200px] max-w-[400px]"
        style={{ fontSize: "15px", fontWeight: 600, color: "#1e293b" }}
        placeholder="Nombre de la oferta"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid="hdr-nombre-btn"
      title="Click para editar el nombre"
      className="text-left truncate hover:border-[#cbd5e1] border-b-2 border-transparent px-1 py-0.5 transition min-w-0 max-w-[400px]"
      style={{ fontSize: "15px", fontWeight: 600, color: "#1e293b" }}
    >
      {display}
    </button>
  );
}

function Info({ label, value, testid, onGreen = false }) {
  return (
    <div data-testid={testid}>
      <p className={`text-xs uppercase tracking-wide ${onGreen ? "text-white/50" : "text-slate-500"}`}>{label}</p>
      <p className={`font-semibold truncate ${onGreen ? "text-white" : "text-slate-900"}`}>{value || <span className={onGreen ? "text-white/30" : "text-slate-300"}>—</span>}</p>
    </div>
  );
}

function NumField({ label, value, onChange, step = "1", testid }) {
  // IMPL-070 · onFocus select para permitir sobrescribir un valor existente sin tener que borrar antes.
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input
        type="number"
        value={value === 0 || value == null ? "" : value}
        placeholder="0"
        step={step}
        onFocus={(e) => e.target.select()}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => { if (e.target.value === "") onChange(0); }}
        data-testid={testid}
        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function VehiculoRow({ veh, tipos, usos, onChange, onDelete, idx }) {
  /**
   * IMPL-069 · Autofill de defaults al cambiar Tipo / Uso desde la Config maestra (DEC-021).
   *   - Tipo aporta: valor_unitario, consumo_base, siniestralidad_base, coste_medio_siniestro
   *   - Uso aporta: km_anio_tipico + multiplicadores (consumo, siniestros)
   * Fórmula final:
   *   consumo   = tipo.consumo_base × uso.multiplicador_consumo
   *   sin_pct   = tipo.siniestralidad_base × uso.multiplicador_siniestros
   * `uds` NUNCA se rellena por defecto — siempre lo mete el comercial.
   * Regla: siempre sobrescribe (opción C) + botón "Restablecer defaults" visible.
   */
  const defaultsFrom = (tipoId, usoId) => {
    const t = tipos.find((x) => x.id === tipoId);
    const u = usos.find((x) => x.id === usoId);
    const patch = {};
    if (t) {
      patch.valor = t.valor_unitario || 0;
      patch.coste_siniestro = t.coste_medio_siniestro || 0;
    }
    if (t && u) {
      patch.consumo = +((t.consumo_base || 0) * (u.multiplicador_consumo || 1)).toFixed(2);
      patch.sin_pct = +((t.siniestralidad_base || 0) * (u.multiplicador_siniestros || 1)).toFixed(2);
    } else if (t) {
      patch.consumo = t.consumo_base || 0;
      patch.sin_pct = t.siniestralidad_base || 0;
    }
    if (u) patch.km_anio = u.km_anio_tipico || 0;
    return patch;
  };

  const onTipoChange = (nuevoTipoId) => {
    onChange({ tipo_id: nuevoTipoId, ...defaultsFrom(nuevoTipoId, veh.uso_id) });
  };
  const onUsoChange = (nuevoUsoId) => {
    onChange({ uso_id: nuevoUsoId, ...defaultsFrom(veh.tipo_id, nuevoUsoId) });
  };
  const onRestablecerDefaults = () => {
    onChange(defaultsFrom(veh.tipo_id, veh.uso_id));
  };

  return (
    <div className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`vehiculo-row-${idx}`}>
      <div className="col-span-3">
        <label className="block text-[10px] uppercase text-slate-500">Tipo</label>
        <select value={veh.tipo_id} onChange={(e) => onTipoChange(e.target.value)}
          className="w-full px-2 py-1.5 border rounded text-sm bg-white"
          data-testid={`veh-${idx}-tipo`}>
          <option value="">—</option>
          {tipos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
        </select>
      </div>
      <div className="col-span-3">
        <label className="block text-[10px] uppercase text-slate-500">Uso</label>
        <select value={veh.uso_id} onChange={(e) => onUsoChange(e.target.value)}
          className="w-full px-2 py-1.5 border rounded text-sm bg-white"
          data-testid={`veh-${idx}-uso`}>
          <option value="">—</option>
          {usos.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
        </select>
      </div>
      {[
        ["uds", "Uds"], ["valor", "Valor€"], ["consumo", "L/100"], ["km_anio", "km/año"], ["sin_pct", "%sin"],
      ].map(([k, lab]) => (
        <div className="col-span-1" key={k}>
          <label className="block text-[10px] uppercase text-slate-500">
            {k === "sin_pct" ? (
              <span className="relative group inline-flex items-center">
                {lab}
                <span className="ml-1 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-slate-300 text-slate-600 text-[9px] font-bold cursor-help" data-testid="sin-pct-tooltip-trigger">?</span>
                <span className="absolute bottom-5 left-0 hidden group-hover:block bg-slate-800 text-white text-[11px] normal-case px-2 py-1.5 rounded-lg whitespace-nowrap z-30 shadow-lg" data-testid="sin-pct-tooltip">
                  Siniestralidad estimada como % del valor del vehículo al año
                </span>
              </span>
            ) : lab}
          </label>
          <input
            type="number"
            value={veh[k] === 0 || veh[k] == null ? "" : veh[k]}
            placeholder="0"
            onFocus={(e) => e.target.select()}
            onChange={(e) => onChange({ [k]: e.target.value === "" ? 0 : +e.target.value })}
            onBlur={(e) => { if (e.target.value === "") onChange({ [k]: 0 }); }}
            className="w-full px-2 py-1.5 border rounded text-sm" data-testid={`veh-${idx}-${k}`} />
        </div>
      ))}
      <div className="col-span-1 flex items-center gap-0.5 justify-end">
        <button
          onClick={onRestablecerDefaults}
          disabled={!veh.tipo_id && !veh.uso_id}
          title="Restablecer defaults desde Tipo y Uso"
          data-testid={`veh-${idx}-restablecer-defaults`}
          className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onDelete}
          data-testid={`veh-${idx}-delete`}
          className="p-1.5 text-rose-500 hover:bg-rose-50 rounded"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function ProductosCard({ tipo, titulo, lista, minMargen, condicionesSugeridas = [], onAplicarCondicion, onAdd, onChange, onDelete, testid }) {
  return (
    <Card title={titulo}>
      {lista.length === 0 && <p className="text-xs text-slate-400 italic mb-2">Sin productos. Añade el primero.</p>}
      {lista.map((p, i) => {
        const malMargen = p.margen_pct < minMargen;
        return (
          <div key={i} className="mb-3" data-testid={`${testid}-row-${i}`}>
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-3">
                <label className="block text-[10px] uppercase text-slate-500">Nombre</label>
                <input type="text" value={p.nombre} onChange={(e) => onChange(i, { nombre: e.target.value })}
                  className="w-full px-2 py-1.5 border rounded text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] uppercase text-slate-500">SKU</label>
                <input type="text" value={p.sku || ""} onChange={(e) => onChange(i, { sku: e.target.value })}
                  className="w-full px-2 py-1.5 border rounded text-sm" />
              </div>
              <NumCell label="Uds" value={p.uds} onChange={(v) => onChange(i, { uds: +v })} />
              <NumCell label="Coste€" value={p.coste_snapshot} onChange={(v) => onChange(i, { coste_snapshot: +v })} />
              <NumCell label="Precio€" value={p.precio_snapshot} onChange={(v) => onChange(i, { precio_snapshot: +v })} />
              <NumCell label="Dto%" value={p.descuento_pct} onChange={(v) => onChange(i, { descuento_pct: +v })} />
              <div className={`col-span-1 px-2 py-1 rounded text-xs font-bold text-center ${malMargen ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}
                   title={malMargen ? `Mínimo ${minMargen}%` : ""}>
                {pct(p.margen_pct)}
              </div>
              <button onClick={() => onDelete(i)} className="col-span-1 p-2 text-rose-500 hover:bg-rose-50 rounded">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            {/* Chips de descuento sugerido (DEC-007) */}
            {condicionesSugeridas.length > 0 && (
              <div className="mt-1 ml-0 flex flex-wrap items-center gap-1" data-testid={`${testid}-chips-${i}`}>
                <span className="text-[10px] text-slate-400 uppercase tracking-wide">Sugeridos:</span>
                {condicionesSugeridas.map((c) => {
                  const activo = Number(p.descuento_pct) === Number(c.valor);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => onAplicarCondicion(i, c)}
                      data-testid={`${testid}-chip-${i}-${c.id}`}
                      className={`px-2 py-0.5 text-[11px] rounded-full border transition ${
                        activo
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-slate-600 border-slate-200 hover:bg-emerald-50 hover:border-emerald-300"
                      }`}
                      title={c.descripcion || c.nombre}
                    >
                      {c.nombre} · {c.valor}{c.unidad || ""}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      <button onClick={onAdd} data-testid={`${testid}-add`} className="inline-flex items-center gap-1 py-2 px-3 mt-2 text-sm border-dashed border-2 rounded-lg text-slate-500 hover:bg-slate-50 self-start">
        <Plus className="w-4 h-4" /> Añadir
      </button>
    </Card>
  );
}

function NumCell({ label, value, onChange }) {
  // IMPL-070 · Fix UX: mostrar string vacío cuando el valor es 0 para evitar "0X" pegado al tipear.
  // Al hacer focus, seleccionar todo por si el usuario quiere sobreescribir un valor existente.
  return (
    <div className="col-span-1">
      <label className="block text-[10px] uppercase text-slate-500">{label}</label>
      <input
        type="number"
        value={value === 0 || value == null ? "" : value}
        placeholder="0"
        onFocus={(e) => e.target.select()}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => { if (e.target.value === "") onChange(0); }}
        className="w-full px-2 py-1.5 border rounded text-sm"
      />
    </div>
  );
}

function Acordeon({ open, setOpen, icon, color, titulo, resumen, children, testid }) {
  return (
    <div className={`rounded-xl border-2 ${color}`} data-testid={testid}>
      <button onClick={setOpen} className="w-full px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">{icon}<span className="font-semibold text-slate-900">{titulo}</span></div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-slate-900">{resumen}</span>
          {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </div>
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t bg-white/50">{children}</div>}
    </div>
  );
}

function Linea({ concepto, sub, valor, bold = false, positive = null }) {
  const color = positive === null ? "text-slate-900" : (positive ? "text-emerald-700" : "text-rose-700");
  return (
    <div className="flex items-baseline justify-between py-1 text-sm">
      <div className={bold ? "font-semibold" : ""}>
        {concepto}
        {sub && <span className="text-xs text-slate-500 ml-2">{sub}</span>}
      </div>
      <span className={`tabular-nums ${color} ${bold ? "font-bold" : ""}`}>{eur(valor)}</span>
    </div>
  );
}

// ============ Picker de catálogo ============
function PickerCatalogo({ tipo, catalogo, onClose, onPick, onAddManual }) {
  const [q, setQ] = useState("");
  const filtrados = (catalogo || [])
    .filter((p) => p.tipo === tipo)
    .filter((p) => !q || `${p.sku} ${p.nombre} ${p.categoria || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl flex flex-col" data-testid="picker-catalogo">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="font-bold text-slate-900">
            Añadir producto {tipo === "hw" ? "HW" : "SaaS"} desde catálogo
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input autoFocus type="text" placeholder="Buscar por SKU, nombre o categoría…"
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="picker-search"
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
        </div>
        <div className="max-h-[50vh] overflow-y-auto">
          {filtrados.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              {catalogo.length === 0
                ? "Catálogo vacío. Configúralo en Configuración · FASE 2."
                : "Sin coincidencias."}
            </div>
          ) : (
            <div className="divide-y">
              {filtrados.map((p) => (
                <button key={p.id} onClick={() => onPick(p)}
                  data-testid={`picker-item-${p.id}`}
                  className="w-full px-5 py-3 hover:bg-emerald-50 flex items-center justify-between text-left">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{p.nombre}</p>
                    <p className="text-xs text-slate-500">
                      <code>{p.sku}</code>
                      {p.categoria && <span> · {p.categoria}</span>}
                      <span> · coste {eur(p.coste)} · precio {eur(p.precio)}</span>
                    </p>
                  </div>
                  <Plus className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-between items-center bg-slate-50 rounded-b-xl">
          <button onClick={onAddManual} data-testid="picker-manual"
            className="text-sm text-slate-600 hover:text-slate-900">
            + Añadir manualmente (sin catálogo)
          </button>
          <button onClick={onClose} className="text-sm px-3 py-1.5 hover:bg-slate-200 rounded">Cancelar</button>
        </div>
      </div>
    </div>
  );
}


// IMPL-036 (2026-05-13) · helpers de la nueva tabla P&L del paso 2
/**
 * IMPL-064 (2026-06-30) · 2 zonas KPI alineadas con sus columnas (Visión MOBILSAFE · Visión cliente).
 * Reemplaza al ResumenKPIsSticky anterior. Las dos zonas comparten dinámica visual pero distinta paleta:
 *   - MOBILSAFE → navy #1e3a5f (3 KPIs internos)
 *   - CLIENTE   → verde #0a6640 (3 KPIs orientados a cliente)
 */
// IMPL-064 · Helper para las 5 filas de la tabla B1 "Operación por vehículo".
// Extraído a componente porque el plugin visual-edits/babel-metadata-plugin se atasca
// si se usa IIFE+Fragment dentro de JSX. Componente normal funciona sin problemas.
function FilasB1Operacion({ calc, mesesAnalisis, hwFinanciado, minHW, minSAAS }) {
  const meses = Math.max(mesesAnalisis || 1, 1);
  const ventaHWmes  = hwFinanciado ? (calc.cuotaHW_mes_uni || 0) : ((calc.precioHW_uni || 0) / meses);
  const costeHWmes  = (calc.costeHW_uni || 0) / meses;
  const ventaTotMes = ventaHWmes + (calc.precioSaaS_uni || 0);
  const costeTotMes = costeHWmes + (calc.costeSaaS_uni || 0);
  const mbTotMes    = ventaTotMes - costeTotMes;
  const pctTotMes   = ventaTotMes > 0 ? (mbTotMes / ventaTotMes) * 100 : 0;
  return (
    <>
      <FilaTabla label="Venta" cols={[eur(calc.precioHW_uni), eur(calc.precioSaaS_uni), eur(ventaTotMes)]} />
      <FilaTabla label="Coste" cols={[eur(calc.costeHW_uni), eur(calc.costeSaaS_uni), eur(costeTotMes)]} />
      <FilaTabla label="MB"    cols={[eur(calc.mbHW_uni),    eur(calc.mbSaaS_uni),    eur(mbTotMes)]} negrita />
      <FilaTabla label="%MB"   cols={[pct(calc.pctHW),       pct(calc.pctSaaS),       pct(pctTotMes)]} />
      <FilaTabla label="Check" cols={[<Check key="hw" val={calc.pctHW} min={minHW} />, <Check key="saas" val={calc.pctSaaS} min={minSAAS} />, <Check key="totmes" val={pctTotMes} min={minHW} />]} ultima />
    </>
  );
}

function ZonasKPI({ calc, mesesAnalisis, hwFinanciado }) {
  // KPIs MOBILSAFE
  const ingresoMensual = (calc.cuotaSaas_ajustada_mes || 0) + (hwFinanciado ? (calc.cuotaHW_mes_total || 0) : 0);
  const ingresoTotal   = (calc.totalHW || 0) + (calc.cuotasSaas_periodo || 0);
  // KPIs CLIENTE
  const totalBeneficios = calc.balance_operativo || 0;
  const ahorrosAreas    = calc.ahorro_periodo || 0;
  const caesNetoVal     = calc.caesNeto || 0;
  const balanceOk = totalBeneficios >= 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4" data-testid="constructor-zonas-kpi">
      {/* Zona MOBILSAFE · navy */}
      <div className="space-y-2" data-testid="zona-kpi-mobilsafe">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#1e3a5f]/60 px-1">Indicadores internos</div>
        <div className="grid grid-cols-3 gap-3">
          <KPICard iconName="Wallet"     label="Ingreso mensual" value={eur(ingresoMensual)} sub="SaaS recurrente" paleta="ms" testid="kpi-ms-ingreso-mensual" />
          <KPICard iconName="DollarSign" label="% MB total"      value={pct(calc.pctTotal)}  sub="Margen bruto"    paleta="ms" testid="kpi-ms-pct-mb" />
          <KPICard iconName="TrendingUp" label="Ingreso total"   value={eur(ingresoTotal)}   sub={`HW + SaaS · ${mesesAnalisis}m`} paleta="ms" destacado testid="kpi-ms-ingreso-total" />
        </div>
      </div>
      {/* Zona CLIENTE · verde */}
      <div className="space-y-2" data-testid="zona-kpi-cliente">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#0a6640]/70 px-1">Argumento al cliente</div>
        <div className="grid grid-cols-3 gap-3">
          <KPICard iconName="Target"   label="Total beneficios" value={eur(totalBeneficios)} sub={`Balance neto ${mesesAnalisis}m`} paleta="cliente" destacado destacadoColor={balanceOk ? "verde" : "rojo"} testid="kpi-cl-total-beneficios" />
          <KPICard iconName="Star"     label="Ahorros áreas"    value={eur(ahorrosAreas)}    sub={`Comb+Mant+Sin+Mul · ${mesesAnalisis}m`} paleta="cliente" testid="kpi-cl-ahorros" />
          <KPICard iconName="Diamond"  label="CAES neto"        value={eur(caesNetoVal)}     sub="Año 1 · única"    paleta="cliente" testid="kpi-cl-caes" />
        </div>
      </div>
    </div>
  );
}

// IMPL-064 · Mapa interno de iconos para evitar bug del plugin visual-edits
// (el plugin se atasca trazando refs de componentes pasadas como prop entre archivos).
const KPI_ICON_MAP = { Wallet, DollarSign, TrendingUp, Target, Star, Diamond };

function KPICard({ iconName, label, value, sub, paleta = "ms", destacado = false, destacadoColor = "verde", testid }) {
  const Icon = KPI_ICON_MAP[iconName] || Wallet;
  const palettes = {
    ms: {
      bg: destacado ? "bg-[#1e3a5f] text-white" : "bg-white",
      border: "border-[#1e3a5f]/30",
      icon:  destacado ? "text-amber-300" : "text-[#1e3a5f]",
      label: destacado ? "text-white/70" : "text-[#1e3a5f]/70",
      value: destacado ? "text-white" : "text-[#1e3a5f]",
      sub:   destacado ? "text-white/60" : "text-slate-400",
    },
    cliente: {
      bg: destacado ? (destacadoColor === "rojo" ? "bg-rose-700 text-white" : "bg-[#0a6640] text-white") : "bg-white",
      border: "border-[#0a6640]/30",
      icon:  destacado ? "text-amber-300" : "text-[#0a6640]",
      label: destacado ? "text-white/70" : "text-[#0a6640]/70",
      value: destacado ? "text-white" : "text-[#0a6640]",
      sub:   destacado ? "text-white/60" : "text-slate-400",
    },
  };
  const p = palettes[paleta];
  return (
    <div className={`rounded-lg border-2 ${p.border} ${p.bg} px-4 py-3 transition`} data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${p.icon}`} />
        <div className={`text-[10px] uppercase tracking-wider font-bold ${p.label}`}>{label}</div>
      </div>
      <div className={`text-2xl font-black tabular-nums mt-1 ${p.value}`}>{value}</div>
      {sub && <div className={`text-[11px] mt-0.5 ${p.sub}`}>{sub}</div>}
    </div>
  );
}

function BloqueTabla({ titulo, colorHeader, paletaName, headers, children, testid }) {
  // IMPL-060 · acordeón cerrado por defecto. Click en cabecera abre/cierra.
  const [open, setOpen] = useState(false);
  const paleta = derivePaleta(colorHeader, paletaName);
  // Pasamos la paleta al contexto local — los FilaTabla la leerán por React.cloneElement
  const childrenWithPaleta = React.Children.map(children, (child) =>
    React.isValidElement(child) ? React.cloneElement(child, { __paleta: paleta }) : child
  );
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={testid ? `${testid}-toggle` : undefined}
        className={`w-full px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center justify-between gap-2 ${colorHeader}`}
      >
        <span className="text-left">{titulo}</span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <table className="w-full text-xs">
          {Array.isArray(headers) && headers.length > 0 && (
            <thead>
              <tr className={`border-b border-slate-200 ${paleta.colHeader}`}>
                {headers.map((h, i) => (
                  <th key={i} className={`px-4 py-1.5 text-[10px] font-black ${paleta.labelText} uppercase tracking-wider ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>{childrenWithPaleta}</tbody>
        </table>
      )}
    </div>
  );
}

// IMPL-039 (2026-06-30) · Paletas escalonadas para tablas con shading reforzado.
// Derivadas del nombre del color del header (bg-{color}-100). Para colores especiales
// como "bg-[#0a6640]" (Visión Cliente), pasar paleta explícita en `paletaName`.
const PALETAS_TABLA = {
  orange:  { colHeader: "bg-orange-100/70",  colLabel: "bg-orange-100/70",  rowAlt: "bg-orange-50/60",  kpiBg: "bg-orange-200",  kpiText: "text-orange-950",  labelText: "text-orange-900" },
  amber:   { colHeader: "bg-amber-100/70",   colLabel: "bg-amber-100/70",   rowAlt: "bg-amber-50/60",   kpiBg: "bg-amber-200",   kpiText: "text-amber-950",   labelText: "text-amber-900" },
  blue:    { colHeader: "bg-blue-100/70",    colLabel: "bg-blue-100/70",    rowAlt: "bg-blue-50/60",    kpiBg: "bg-blue-200",    kpiText: "text-blue-950",    labelText: "text-blue-900" },
  emerald: { colHeader: "bg-emerald-100/70", colLabel: "bg-emerald-100/70", rowAlt: "bg-emerald-50/60", kpiBg: "bg-emerald-200", kpiText: "text-emerald-950", labelText: "text-emerald-900" },
  green:   { colHeader: "bg-emerald-100/70", colLabel: "bg-emerald-100/70", rowAlt: "bg-emerald-50/60", kpiBg: "bg-emerald-200", kpiText: "text-emerald-950", labelText: "text-emerald-900" },
  violet:  { colHeader: "bg-violet-100/70",  colLabel: "bg-violet-100/70",  rowAlt: "bg-violet-50/60",  kpiBg: "bg-violet-200",  kpiText: "text-violet-950",  labelText: "text-violet-900" },
  purple:  { colHeader: "bg-purple-100/70",  colLabel: "bg-purple-100/70",  rowAlt: "bg-purple-50/60",  kpiBg: "bg-purple-200",  kpiText: "text-purple-950",  labelText: "text-purple-900" },
  rose:    { colHeader: "bg-rose-100/70",    colLabel: "bg-rose-100/70",    rowAlt: "bg-rose-50/60",    kpiBg: "bg-rose-200",    kpiText: "text-rose-950",    labelText: "text-rose-900" },
  // Variante "verde oscuro Visión Cliente": header sólido #0a6640, body con tintes emerald
  dark:    { colHeader: "bg-emerald-100/70", colLabel: "bg-emerald-100/70", rowAlt: "bg-emerald-50/60", kpiBg: "bg-emerald-200", kpiText: "text-emerald-950", labelText: "text-emerald-900" },
  slate:   { colHeader: "bg-slate-100",      colLabel: "bg-slate-100",      rowAlt: "bg-slate-50/60",   kpiBg: "bg-slate-200",   kpiText: "text-slate-900",   labelText: "text-slate-700" },
};
function derivePaleta(colorHeader, paletaName) {
  if (paletaName && PALETAS_TABLA[paletaName]) return PALETAS_TABLA[paletaName];
  if (!colorHeader) return PALETAS_TABLA.slate;
  const m = colorHeader.match(/bg-(\w+)-\d+/);
  if (m && PALETAS_TABLA[m[1]]) return PALETAS_TABLA[m[1]];
  if (colorHeader.includes("#0a6640") || colorHeader.includes("text-white")) return PALETAS_TABLA.dark;
  return PALETAS_TABLA.slate;
}

function FilaTabla({ label, cols = [], ultima, negrita, verde, rojo, __paleta }) {
  const paleta = __paleta || PALETAS_TABLA.slate;
  if (negrita) {
    // KPI · banda completa con fondo del color del header
    return (
      <tr className={paleta.kpiBg}>
        <td className={`px-4 py-2 text-left text-[11px] font-black uppercase tracking-wider ${paleta.kpiText} ${ultima ? "" : "border-b border-white/70"}`}>{label}</td>
        {cols.map((v, i) => (
          <td key={i} className={`px-4 py-2 text-right text-[14px] font-black ${paleta.kpiText} ${ultima ? "" : "border-b border-white/70"}`}>{v}</td>
        ))}
      </tr>
    );
  }
  const colorCls = verde ? "text-emerald-700" : rojo ? "text-red-600" : "text-slate-900";
  return (
    <tr>
      <td className={`px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider ${paleta.labelText} ${paleta.colLabel} ${ultima ? "border-b-0" : "border-b border-slate-100"}`}>{label}</td>
      {cols.map((v, i) => <td key={i} className={`px-4 py-2 text-right text-[13px] font-bold ${colorCls} ${ultima ? "border-b-0" : "border-b border-slate-100"}`}>{v}</td>)}
    </tr>
  );
}

function Check({ val, min }) {
  const ok = val >= min;
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${ok ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-rose-100 text-rose-700 border-rose-200"}`}>
      {ok ? "OK" : "BAJO"}
    </span>
  );
}

// IMPL-039 Fase 2 · KPI vivo del header (Header B)
function KpiVivoHdr({ Icon, label, valor, delta, color }) {
  const palette = {
    emerald: { iconBg: "bg-emerald-100", iconText: "text-emerald-600", valorText: "text-emerald-700", deltaBg: "bg-emerald-200/60 text-emerald-800" },
    amber:   { iconBg: "bg-amber-100",   iconText: "text-amber-600",   valorText: "text-amber-700",   deltaBg: "bg-amber-200/60 text-amber-800" },
    blue:    { iconBg: "bg-blue-100",    iconText: "text-blue-600",    valorText: "text-blue-700",    deltaBg: "bg-blue-200/60 text-blue-800" },
  }[color] || { iconBg: "bg-slate-100", iconText: "text-slate-600", valorText: "text-slate-700", deltaBg: "bg-slate-200/60 text-slate-800" };
  return (
    <div className="flex items-center gap-3" data-testid={`hdr-kpi-${color}`}>
      <div className={`w-10 h-10 rounded-lg ${palette.iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 ${palette.iconText}`} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</p>
        <div className="flex items-baseline gap-2 flex-wrap">
          <p className={`text-lg font-black ${palette.valorText} leading-tight truncate`}>{valor}</p>
          {delta && <span className={`text-[10px] px-1.5 py-0.5 rounded ${palette.deltaBg} font-bold flex-shrink-0`}>{delta}</span>}
        </div>
      </div>
    </div>
  );
}

// IMPL-023 · Navegación inferior entre pasos del Constructor
function NavPasoConstructor({ paso, setPaso }) {
  // IMPL-039 (2026-06-30) · en el último paso el botón "Siguiente paso" se ocultaba
  // como disabled — confundía al comercial. Ahora lo escondemos en paso 2.
  const esUltimoPaso = paso === 2;
  return (
    <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
      <button
        onClick={() => setPaso(Math.max(1, paso - 1))}
        disabled={paso === 1}
        data-testid={`constructor-paso-${paso}-anterior`}
        className="px-4 py-2 text-sm text-slate-700 border border-slate-300 rounded-full hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-2"
      >
        <ArrowLeft className="w-4 h-4" /> Paso anterior
      </button>
      <span className="text-xs text-slate-500 font-mono">{paso} / 2</span>
      {esUltimoPaso ? (
        // Placeholder vacío para mantener el space-between · NO botón muerto.
        <span aria-hidden="true" className="w-[148px]" />
      ) : (
        <button
          onClick={() => setPaso(Math.min(2, paso + 1))}
          data-testid={`constructor-paso-${paso}-siguiente`}
          className="px-5 py-2 text-sm bg-[#0a6640] text-white rounded-full font-semibold hover:bg-[#085030] inline-flex items-center gap-2"
        >
          Siguiente paso <ArrowLeft className="w-4 h-4 rotate-180" />
        </button>
      )}
    </div>
  );
}

